/**
 * Auto-discovered local competitors card — table of businesses appearing in the same
 * map pack as the client. Each row gets an inline "Add" or "Tracked" button so the user
 * can promote a competitor straight into the tracked-competitors list.
 */
import React from "react";
import { MapPin, Plus, Loader2, Check } from "lucide-react";
import { fmt, normDomain } from "../../../lib/format";
import Stars from "./Stars";

function CompetitorRow({ c, index, tracked, adding, onAdd }) {
  const hasDomain = !!(c.domain && c.domain.trim());
  return (
    <div
      data-testid={`local-competitor-row-${index}`}
      className="grid px-5 py-3 hover:bg-[#f7fafc] text-sm items-center"
      style={{ gridTemplateColumns: "repeat(13, minmax(0, 1fr))" }}
    >
      <div className="col-span-1 font-mono-tight text-[#a0aec0]">{index + 1}</div>
      <div className="col-span-4 min-w-0">
        <div className="text-[#1a202c] truncate" title={c.title}>{c.title}</div>
        <div className="text-[10px] text-[#a0aec0] font-mono-tight truncate" title={c.address}>
          {c.domain && (
            <a href={c.url || `https://${c.domain}`} target="_blank" rel="noreferrer" className="hover:text-[#2c7a7b]">
              {c.domain}
            </a>
          )}
          {c.domain && c.address && <span className="mx-1.5">·</span>}
          {c.address}
        </div>
      </div>
      <div className="col-span-2 text-right">
        <span className="inline-flex items-center justify-end gap-1 font-mono-tight">
          <span className="text-[#1a202c] font-semibold">{c.appearances}</span>
          <span className="text-[10px] text-[#a0aec0]">× kw</span>
        </span>
      </div>
      <div className="col-span-1 text-right font-mono-tight font-semibold">
        {c.best_rank != null ? <span className="text-[#38b2ac]">#{c.best_rank}</span> : "—"}
      </div>
      <div className="col-span-1 text-right font-mono-tight text-[#4a5568]">
        {c.avg_rank != null ? `#${c.avg_rank}` : "—"}
      </div>
      <div className="col-span-2 text-right">
        <div className="inline-flex flex-col items-end">
          <Stars value={c.rating_value} />
          <span className="text-[10px] text-[#a0aec0] font-mono-tight">{fmt(c.rating_votes)} reviews</span>
        </div>
      </div>
      <div className="col-span-2 text-right">
        {!hasDomain ? (
          <span
            className="text-[10px] text-[#cbd5e0] font-mono-tight"
            title="DataForSEO didn't return a website for this listing, so it can't be added by domain"
          >
            no website
          </span>
        ) : tracked ? (
          <span
            className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest px-2.5 py-1.5 rounded-lg bg-[#4fd1c5]/10 text-[#234e52]"
            data-testid={`local-competitor-added-${index}`}
          >
            <Check className="h-3 w-3" strokeWidth={2.6} /> Tracked
          </span>
        ) : (
          <button
            type="button"
            onClick={onAdd}
            disabled={adding}
            data-testid={`local-competitor-add-${index}`}
            className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest px-2.5 py-1.5 rounded-lg border border-[#e2e8f0] hover:border-[#4fd1c5] hover:text-[#2c7a7b] text-[#4a5568] bg-white disabled:opacity-50 transition-colors"
          >
            {adding ? <Loader2 className="h-3 w-3 animate-spin" /> : <Plus className="h-3 w-3" strokeWidth={2.6} />}
            {adding ? "Adding…" : "Add"}
          </button>
        )}
      </div>
    </div>
  );
}

export default function LocalCompetitorsCard({ competitors, trackedSet, addingDomain, onAddCompetitor }) {
  return (
    <div className="card-soft overflow-hidden" data-testid="local-competitors-card">
      <div className="px-5 py-4 border-b border-[#edf2f7] flex items-end justify-between flex-wrap gap-3">
        <div className="flex items-start gap-3">
          <div className="h-9 w-9 rounded-xl bg-[#4fd1c5]/10 text-[#2c7a7b] inline-flex items-center justify-center shrink-0">
            <MapPin className="h-4 w-4" />
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-widest text-[#a0aec0] font-semibold">Local Pack Competitors</div>
            <h3 className="font-display text-base mt-0.5 text-[#1a202c]">Who shows up in your map results</h3>
          </div>
        </div>
        <div className="text-[10px] text-[#a0aec0] font-mono-tight uppercase tracking-widest">
          {competitors.length} businesses · latest snapshot
        </div>
      </div>
      {competitors.length === 0 ? (
        <div className="p-6 text-center text-xs text-[#a0aec0] font-mono-tight">
          No local competitors yet. Click &ldquo;Refresh Local Finder&rdquo; to discover them.
        </div>
      ) : (
        <>
          <div className="grid px-5 py-2.5 text-[10px] uppercase tracking-widest text-[#a0aec0] font-bold border-b border-[#edf2f7] bg-[#f7fafc]" style={{ gridTemplateColumns: "repeat(13, minmax(0, 1fr))" }}>
            <div className="col-span-1">#</div>
            <div className="col-span-4">Business</div>
            <div className="col-span-2 text-right">Appears</div>
            <div className="col-span-1 text-right">Best</div>
            <div className="col-span-1 text-right">Avg</div>
            <div className="col-span-2 text-right">Rating</div>
            <div className="col-span-2 text-right">Action</div>
          </div>
          <div className="divide-y divide-[#edf2f7]">
            {competitors.slice(0, 15).map((c, i) => (
              <CompetitorRow
                key={c.key}
                c={c}
                index={i}
                tracked={trackedSet.has(normDomain(c.domain))}
                adding={addingDomain === normDomain(c.domain)}
                onAdd={() => onAddCompetitor(c.domain)}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}
